// ============================================================================
// PATIENT REMINDERS BLOCK — Recordatorios del paciente agrupados por estado
// Click en planificado → abre edición. Click en otro → navega al presupuesto.
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Edit2, X, CheckCircle2, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { remindersApi, type ReminderDto } from '../../api/reminders';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { ReminderModal } from '../ui/ReminderModal';
import { toast } from '../ui/Toast';

interface PatientRemindersBlockProps {
  patientId: string;
  patientName: string;
  userId?: string;
  onChanged?: () => void;
}

type Status = 'PENDIENTE' | 'ENVIADO' | 'FALLIDO' | 'CANCELADO';

const STATUS_LABELS: Record<Status, string> = {
  PENDIENTE: 'Planificados',
  ENVIADO: 'Enviados',
  FALLIDO: 'Fallidos',
  CANCELADO: 'Cancelados',
};

const STATUS_ICONS: Record<Status, React.ReactNode> = {
  PENDIENTE: <Clock className="h-4 w-4 text-blue-500" />,
  ENVIADO: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  FALLIDO: <AlertTriangle className="h-4 w-4 text-red-500" />,
  CANCELADO: <XCircle className="h-4 w-4 text-gray-400" />,
};

const INITIAL_LIMIT = 10;

function formatReminderDate(isoDate: string): string {
  try {
    const [year, month, day] = isoDate.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return isoDate;
  }
}

function OutcomeBadge({ outcome }: { outcome: 'CITED' | 'NOT_CITED' | null }) {
  if (outcome === 'CITED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
        <CheckCircle2 className="h-3 w-3" /> Con cita
      </span>
    );
  }
  if (outcome === 'NOT_CITED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
        <XCircle className="h-3 w-3" /> Sin cita
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
      Sin gestionar
    </span>
  );
}

export function PatientRemindersBlock({ patientId, patientName, userId, onChanged }: PatientRemindersBlockProps) {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<ReminderDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [toCancel, setToCancel] = useState<ReminderDto | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [toEdit, setToEdit] = useState<ReminderDto | null>(null);

  // Límites de paginación por grupo
  const [limits, setLimits] = useState<Record<Status, number>>({
    PENDIENTE: INITIAL_LIMIT,
    ENVIADO: INITIAL_LIMIT,
    FALLIDO: INITIAL_LIMIT,
    CANCELADO: INITIAL_LIMIT,
  });

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await remindersApi.getAll({ patientId });
      setReminders(result);
      setHasLoaded(true);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar recordatorios');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const grouped: Record<Status, ReminderDto[]> = {
    PENDIENTE: [],
    ENVIADO: [],
    FALLIDO: [],
    CANCELADO: [],
  };
  for (const r of reminders) {
    if (r.status in grouped) grouped[r.status as Status].push(r);
  }

  // Orden: PENDIENTE asc (próximos primero), resto desc (más reciente primero)
  grouped.PENDIENTE.sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
  grouped.ENVIADO.sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate));
  grouped.FALLIDO.sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate));
  grouped.CANCELADO.sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate));

  const handleCancel = async () => {
    if (!toCancel) return;
    setIsCancelling(true);
    try {
      await remindersApi.cancel(toCancel.id);
      toast.success('Recordatorio cancelado');
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo cancelar');
    } finally {
      setIsCancelling(false);
      setToCancel(null);
    }
  };

  const handleOutcome = async (r: ReminderDto, outcome: 'CITED' | 'NOT_CITED') => {
    try {
      await remindersApi.setOutcome(r.id, outcome);
      toast.success(outcome === 'CITED' ? 'Marcado con cita' : 'Marcado sin cita');
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo actualizar');
    }
  };

  const handleRowClick = (r: ReminderDto) => {
    if (r.status === 'PENDIENTE') {
      setToEdit(r);
    } else if (r.budgetId) {
      navigate(`/presupuestos/${r.budgetId}`);
    }
  };

  const renderGroup = (status: Status, items: ReminderDto[]) => {
    if (items.length === 0) return null;

    const visible = items.slice(0, limits[status]);
    const hasMore = items.length > limits[status];

    return (
      <div key={status}>
        <div className="flex items-center gap-2 mb-2">
          {STATUS_ICONS[status]}
          <h3 className="text-sm font-semibold text-gray-700">
            {STATUS_LABELS[status]}
            <span className="ml-1.5 text-gray-400 font-normal">({items.length})</span>
          </h3>
        </div>
        <div className="space-y-2 mb-4">
          {visible.map((r) => {
            const canManageOutcome = (r.status === 'ENVIADO' || r.status === 'FALLIDO') && !r.appointmentOutcome;
            return (
              <div
                key={r.id}
                className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => handleRowClick(r)}
                    className="flex-1 text-left min-w-0"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">
                        {formatReminderDate(r.scheduledDate)}
                      </span>
                      {r.budgetNumber && (
                        <span className="text-xs font-mono text-gray-500">· {r.budgetNumber}</span>
                      )}
                      {r.templateName && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          {r.templateName}
                        </span>
                      )}
                      {r.status === 'ENVIADO' && <OutcomeBadge outcome={r.appointmentOutcome} />}
                      {r.status === 'FALLIDO' && r.failReasonLabel && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                          {r.failReasonLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{r.resolvedMessage}</p>
                  </button>

                  <div className="flex items-center gap-1 shrink-0">
                    {r.status === 'PENDIENTE' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setToEdit(r)}
                          className="p-1.5 text-gray-400 hover:text-[#008DD0] transition-colors"
                          title="Editar"
                          aria-label="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setToCancel(r)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Cancelar"
                          aria-label="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {canManageOutcome && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleOutcome(r, 'CITED')}
                          className="text-xs px-2 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50 font-medium"
                        >
                          Con cita
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOutcome(r, 'NOT_CITED')}
                          className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                        >
                          Sin cita
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {hasMore && (
            <button
              type="button"
              onClick={() => setLimits((prev) => ({ ...prev, [status]: prev[status] + INITIAL_LIMIT }))}
              className="w-full py-2 text-sm text-[#008DD0] hover:text-[#007ab8] font-medium"
            >
              Ver {Math.min(INITIAL_LIMIT, items.length - limits[status])} más
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <CollapsibleSection
        id="reminders"
        title="Recordatorios"
        icon={<Bell className="h-5 w-5 text-gray-400" />}
        badge={
          <span className="text-sm font-normal text-gray-500">
            {reminders.length} {reminders.length === 1 ? 'recordatorio' : 'recordatorios'}
          </span>
        }
        defaultOpen={false}
        userId={userId}
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm mb-3">
            {error}
          </div>
        )}
        {isLoading && !hasLoaded ? (
          <p className="text-sm text-gray-400">Cargando...</p>
        ) : reminders.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">
            Este paciente aún no tiene recordatorios programados.
          </p>
        ) : (
          <div>
            {renderGroup('PENDIENTE', grouped.PENDIENTE)}
            {renderGroup('ENVIADO', grouped.ENVIADO)}
            {renderGroup('FALLIDO', grouped.FALLIDO)}
            {renderGroup('CANCELADO', grouped.CANCELADO)}
          </div>
        )}
      </CollapsibleSection>

      <ConfirmDialog
        isOpen={!!toCancel}
        onClose={() => !isCancelling && setToCancel(null)}
        onConfirm={handleCancel}
        isLoading={isCancelling}
        variant="warning"
        title="¿Cancelar recordatorio?"
        message={toCancel ? `El recordatorio programado para el ${formatReminderDate(toCancel.scheduledDate)} será cancelado y no se enviará.` : ''}
        confirmText="Cancelar recordatorio"
      />

      {toEdit && (
        <ReminderModal
          isOpen={!!toEdit}
          onClose={() => setToEdit(null)}
          onSaved={() => { load(); onChanged?.(); }}
          patientId={patientId}
          patientName={patientName}
          budgetId={toEdit.budgetId || undefined}
          budgetNumber={toEdit.budgetNumber || undefined}
          treatmentName={toEdit.treatmentName || undefined}
          pendingAmount={toEdit.pendingAmount}
          reminderToEdit={toEdit}
        />
      )}
    </>
  );
}

export default PatientRemindersBlock;
